import React, { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { FlexWrapper } from "components/AgGrid/TableWrapper";

import { AgGridTables } from "constants/aggrid";
import {
  BillingDocumentColId,
  BillingDocumentColumnDef,
  BusinessColumnGroup,
  RetainedProceedsColId,
  RetainedProceedsColumnDef,
  XeroIntegrationBusinessColId,
  XeroIntegrationBusinessDef,
} from "constants/columnDefinitions";
import { SaleType } from "constants/sale";

import {
  getActiveRole,
  getCurrentSale,
  selectActiveBillingRunBillingDocumentsAggridData,
} from "selectors";

import { useBillingDocumentAggridGetContextMenuItems } from "hooks/useBillingDocumentAggridGetContextMenuItems";

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: "agMultiColumnFilter",
  enableCellChangeFlash: true,
  floatingFilter: true,
  flex: true,
};

const billingTableColumnDefs = saleType => {
  const isReferenceSale = saleType === SaleType.REFERENCE_SALE;
  return [
    {
      headerName: "Documents",
      children: [
        BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_TYPE],
        BillingDocumentColumnDef[BillingDocumentColId.DOCUMENT_NUMBER],
        BillingDocumentColumnDef[BillingDocumentColId.VENDOR_NUMBER],
        !isReferenceSale &&
          BillingDocumentColumnDef[BillingDocumentColId.VENDOR_COMMISSION],
        !isReferenceSale &&
          XeroIntegrationBusinessDef[
            XeroIntegrationBusinessColId.XERO_INTEGRATION_BUSINESS
          ],
        BillingDocumentColumnDef[BillingDocumentColId.STATUS],
        BillingDocumentColumnDef[BillingDocumentColId.INTEGRATION_STATUS],
        BillingDocumentColumnDef[BillingDocumentColId.DUE_DATE],
        BillingDocumentColumnDef[BillingDocumentColId.TOTAL_AMOUNT_DOLLARS],
        BillingDocumentColumnDef[BillingDocumentColId.TERMS],
        BillingDocumentColumnDef[
          BillingDocumentColId.INTEGRATION_AMOUNT_DIFFERENCE
        ],
        BillingDocumentColumnDef[
          BillingDocumentColId.INTEGRATION_DUE_DIFFERENCE
        ],
        !isReferenceSale &&
          BillingDocumentColumnDef[BillingDocumentColId.HAS_CHANGES],
        BillingDocumentColumnDef[BillingDocumentColId.BILLING_REFERENCE],
        BillingDocumentColumnDef[
          BillingDocumentColId.CALCULATED_BILLING_REFERENCE
        ],
        BillingDocumentColumnDef[BillingDocumentColId.ACTIONS],
        !isReferenceSale &&
          BillingDocumentColumnDef[BillingDocumentColId.ACTIVITY],
      ].filter(Boolean),
    },
    BusinessColumnGroup,
    !isReferenceSale && {
      headerName: "Retained Proceeds",
      children: [
        {
          ...RetainedProceedsColumnDef[
            RetainedProceedsColId.TOTAL_AMOUNT_CENTS
          ],
          hide: true,
        },
        {
          ...RetainedProceedsColumnDef[RetainedProceedsColId.NUMBER],
          hide: true,
        },
      ],
    },
  ].filter(Boolean);
};

// Highlight rows that need attention.
const rowClassRules = {
  "row-warning": params =>
    params.data?.billingDocument.hasFinancialChanges ||
    params.data?.exceptions?.length > 0,
};

const getRowId = params => params.data.billingDocument.id;

export const BillingDocumentsTable = ({
  onGridReady,
  setBillingDocumentId,
  setSelectedBillingDocuments,
  onSetViewDirty,
}) => {
  const rowData = useSelector(selectActiveBillingRunBillingDocumentsAggridData);

  const dispatch = useDispatch();

  const saleType = useSelector(getCurrentSale)?.sale_type;

  const columnDefs = React.useMemo(
    () => billingTableColumnDefs(saleType),
    [saleType],
  );

  const setDocumentStatus = useCallback(
    (billingDocumentId, status) => {
      dispatch(
        BillingDocumentAction.update({
          id: billingDocumentId,
          status,
        }),
      );
    },
    [dispatch],
  );

  const onSelectionChanged = useCallback(
    selectedRows => {
      if (selectedRows.length === 1) {
        setBillingDocumentId(selectedRows[0].billingDocument.id);
      }
      setSelectedBillingDocuments(selectedRows.map(r => r.billingDocument));
    },
    [setBillingDocumentId, setSelectedBillingDocuments],
  );

  const getContextMenuItems = useBillingDocumentAggridGetContextMenuItems();

  const userRoleSlug = useSelector(state => getActiveRole(state).slug);

  const context = useMemo(
    () => ({ setDocumentStatus, userRoleSlug }),
    [setDocumentStatus, userRoleSlug],
  );

  return (
    <AgGridTable
      columnDefs={columnDefs}
      getRowId={getRowId}
      rowClassRules={rowClassRules}
      onGridReady={onGridReady}
      rowData={rowData}
      showGlobalSearchFilter={false}
      hideHeader
      WrapperComponent={FlexWrapper}
      rowSelection={RowSelectionMode.MULTIPLE}
      rowsSelectable
      onRowSelectionChanged={onSelectionChanged}
      defaultColDef={defaultColDef}
      getContextMenuItems={getContextMenuItems}
      context={context}
      tableName={AgGridTables.BILLING_DOCUMENTS}
      onSetViewDirty={onSetViewDirty}
    />
  );
};
